/**
 * PathMediaSections
 *
 * Renders the "path out of the home" media inside the driver's lightbulb /
 * helpful-hints modal:
 *   - Seller path (pickup): video the seller records of the walk from the item
 *     to where the truck parks.
 *   - Buyer path (delivery): video/photos the buyer sends of the path to their door.
 *
 * Videos open in the device browser (no inline player in the app today); photos
 * render as a horizontal scroll and open full-size on tap. Each sub-section hides
 * itself when there's no media. Returns null when there's nothing to show.
 */
import React, { useState } from 'react';
import { View, Text, Image, ScrollView, TouchableOpacity, Linking, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import ImageLightbox from './ImageLightbox';

export type PathMedia = {
  sellerPathVideoUrl?: string | null;
  sellerPathPhotos?: string[] | string | null;
  buyerPathVideoUrl?: string | null;
  buyerPathPhotos?: string[] | string | null;
  buyerPathStairs?: string | null;
};

function toArr(v: string[] | string | null | undefined): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
}

export function hasPathMedia(job: PathMedia | null | undefined): boolean {
  if (!job) return false;
  return !!job.sellerPathVideoUrl || !!job.buyerPathVideoUrl || !!job.buyerPathStairs
    || toArr(job.sellerPathPhotos).length > 0 || toArr(job.buyerPathPhotos).length > 0;
}

function PathBlock({
  label, accent, bg, border, videoUrl, photos, note, onOpen,
}: { label: string; accent: string; bg: string; border: string; videoUrl?: string | null; photos: string[]; note?: string | null; onOpen: (images: string[], index: number) => void }) {
  if (!videoUrl && photos.length === 0 && !note) return null;
  const w = Dimensions.get('window').width;
  // Extra videos ops add to a path (a second clip texted in) ride in the photo list: each gets its
  // own "Watch" button, never an image tile (it would render blank).
  const isVid = (u: string) => /\.(mov|mp4|m4v|webm)(\?|$)/i.test(u);
  const extraVideos = photos.filter(isVid);
  photos = photos.filter((u) => !isVid(u));
  return (
    <View style={{ marginBottom: 14, padding: 12, borderRadius: 10, backgroundColor: bg, borderWidth: 1, borderColor: border }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, gap: 6 }}>
        <Ionicons name="walk-outline" size={16} color={accent} />
        <Text style={{ fontSize: 14, fontWeight: '700', color: accent }}>{label}</Text>
      </View>
      {!!note && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 11, backgroundColor: '#fff', borderRadius: 10, marginBottom: (videoUrl || photos.length) ? 10 : 0, borderWidth: 1, borderColor: border }}>
          <Ionicons name="footsteps-outline" size={18} color={accent} />
          <Text style={{ flex: 1, fontSize: 13.5, fontWeight: '600', color: '#222' }}>Stairs: {note}</Text>
        </View>
      )}
      {!!videoUrl && (
        <TouchableOpacity
          onPress={() => Linking.openURL(videoUrl)}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, backgroundColor: '#fff', borderRadius: 10, marginBottom: photos.length ? 10 : 0, borderWidth: 1, borderColor: border }}
          activeOpacity={0.7}
        >
          <Ionicons name="play-circle" size={28} color={accent} />
          <Text style={{ flex: 1, fontSize: 14, fontWeight: '600', color: '#222' }}>Watch path video</Text>
          <Ionicons name="open-outline" size={16} color="#aaa" />
        </TouchableOpacity>
      )}
      {extraVideos.map((u, i) => (
        <TouchableOpacity key={u}
          onPress={() => Linking.openURL(u)}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, backgroundColor: '#fff', borderRadius: 10, marginBottom: (photos.length || i < extraVideos.length - 1) ? 10 : 0, borderWidth: 1, borderColor: border }}
          activeOpacity={0.7}
        >
          <Ionicons name="play-circle" size={28} color={accent} />
          <Text style={{ flex: 1, fontSize: 14, fontWeight: '600', color: '#222' }}>Watch path video {(videoUrl ? 2 : 1) + i}</Text>
          <Ionicons name="open-outline" size={16} color="#aaa" />
        </TouchableOpacity>
      ))}
      {photos.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginHorizontal: -2 }}>
          {photos.map((uri, i) => (
            <TouchableOpacity key={i} onPress={() => onOpen(photos, i)} activeOpacity={0.8}>
              <Image source={{ uri }} style={{ width: w * 0.5, height: w * 0.38, borderRadius: 10, marginHorizontal: 2 }} resizeMode="cover" />
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
    </View>
  );
}

export function PathMediaSections({ job }: { job: PathMedia | null | undefined }) {
  const [lb, setLb] = useState<{ images: string[]; index: number } | null>(null);
  if (!hasPathMedia(job)) return null;
  const onOpen = (images: string[], index: number) => setLb({ images, index });
  return (
    <View style={{ marginBottom: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10, gap: 6 }}>
        <Ionicons name="map-outline" size={16} color="#666" />
        <Text style={{ fontSize: 14, fontWeight: '700', color: '#222' }}>Path In &amp; Out</Text>
      </View>
      <PathBlock
        label="Seller path (pickup)" accent="#630E3D" bg="#FBF1F6" border="#EBD3DF"
        videoUrl={job?.sellerPathVideoUrl} photos={toArr(job?.sellerPathPhotos)} onOpen={onOpen}
      />
      <PathBlock
        label="Buyer path (delivery)" accent="#1E40AF" bg="#EFF6FF" border="#BFDBFE"
        videoUrl={job?.buyerPathVideoUrl} photos={toArr(job?.buyerPathPhotos)} note={job?.buyerPathStairs} onOpen={onOpen}
      />
      <ImageLightbox images={lb?.images || []} index={lb ? lb.index : null} onClose={() => setLb(null)} />
    </View>
  );
}
